/* eslint-disable eqeqeq */
/* eslint-disable no-continue */
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import { createSelector } from 'reselect';
import { ProviderSummary } from 'store/findProvider/types';
import { currentAccountConsumerIdSelector } from 'store/account/selectors';
import { RootState } from 'store/types';
import { oc } from 'ts-optchain';
import { mapDoctorToType } from 'store/booking/mappers';
import { DoctorConsumerDto, MyDoctorsDto } from './types';
import { mergeArrayObjectsBy } from './utils';

export const myDoctorFetchingSelector = (state: RootState) => get(state, 'myDoctors.isFetching');

export const myDoctorsDTOSelector = (state: RootState): MyDoctorsDto[] =>
  get(state, 'myDoctors.data') || [];

export const doctorSummaryListSelector = (state: RootState): ProviderSummary[] =>
  get(state, 'myDoctors.data.providerSummariesList') || [];

export const pcpDoctorConsumerSelector = (state: RootState): DoctorConsumerDto[] =>
  get(state, 'myDoctors.data.pcp') || [];

export const pastDoctorConsumerSelector = (state: RootState): DoctorConsumerDto[] =>
  get(state, 'myDoctors.data.past') || [];

export const myDoctorRequestStateSelector = (state: RootState) =>
  get(state, 'myDoctors.requestState');

/* function used by the following selectors to find doctor in summary list */
type CommonSummaryDetailDoctorKeys = keyof DoctorConsumerDto & keyof ProviderSummary;

const compareId = (
  docSummary: DoctorConsumerDto,
  doctor: ProviderSummary,
  key: CommonSummaryDetailDoctorKeys
) => {
  return doctor[key] != null && docSummary != null && doctor[key] == docSummary[key];
};

export const isSameDoctor = (docSummary: DoctorConsumerDto, doctor: ProviderSummary) => {
  return ([
    'cernerPersonnelId',
    'cernerResourceId',
    'corpProvId',
    'ncid',
    'npi'
  ] as const).some(key => compareId(docSummary, doctor, key));
};

export const getDoctorListForSelector = (
  doctorConsumerList: DoctorConsumerDto[],
  doctorsList: ProviderSummary[],
  selectedConsumerId?: string
): ProviderSummary[] => {
  const res = [];
  for (const consumerDoctor of doctorConsumerList) {
    if (
      selectedConsumerId &&
      !oc(consumerDoctor)
        .consumerIdList([])
        .includes(selectedConsumerId)
    ) {
      continue;
    }

    const providerSummary = doctorsList.find(doc => isSameDoctor(consumerDoctor, doc));

    if (!providerSummary) {
      continue;
    }

    res.push({ ...providerSummary, isICentraPcp: !!consumerDoctor.isICentraPcp });
  }
  return uniqBy(res, 'corpProvId');
};

export const currentConsumerPastDoctorsSummarySelector = createSelector(
  [pastDoctorConsumerSelector, currentAccountConsumerIdSelector],
  (summaries, consumerId) =>
    summaries.filter(summary => summary.consumerIdList.includes(consumerId))
);

export const currentConsumerPcpDoctorsSummarySelector = createSelector(
  [pcpDoctorConsumerSelector, currentAccountConsumerIdSelector],
  (summaries, consumerId) =>
    summaries.filter(summary => summary.consumerIdList.includes(consumerId))
);

export const pcpDoctorsSelector = createSelector(
  [pcpDoctorConsumerSelector, doctorSummaryListSelector],
  getDoctorListForSelector
);

export const pastDoctorsSelector = createSelector(
  [pastDoctorConsumerSelector, doctorSummaryListSelector],
  getDoctorListForSelector
);

export const currentConsumerPcpDoctorsSelector = createSelector(
  [pcpDoctorConsumerSelector, doctorSummaryListSelector, currentAccountConsumerIdSelector],
  getDoctorListForSelector
);

export const currentConsumerPastDoctorsSelector = createSelector(
  [pastDoctorConsumerSelector, doctorSummaryListSelector, currentAccountConsumerIdSelector],
  getDoctorListForSelector
);

export const currentConsumerAllDoctorsSelector = createSelector(
  [currentConsumerPcpDoctorsSelector, currentConsumerPastDoctorsSelector],
  (pcp, past) => uniqBy([...pcp, ...past], 'cernerPersonnelId')
);

export const currentConsumerAllDoctorsWithTypeSelector = createSelector(
  [currentConsumerPcpDoctorsSelector, currentConsumerPastDoctorsSelector],
  (pcp, past) => {
    const providers = [
      ...pcp.map(dr => mapDoctorToType(dr, 'isPcp')),
      ...past.map(dr => mapDoctorToType(dr, 'isPast'))
    ];
    return mergeArrayObjectsBy(providers, 'corpProvId');
  }
);

export const bookingDoctorsSelector = createSelector(currentConsumerAllDoctorsSelector, doctors =>
  doctors.filter(d => d.isAcceptingOnlineBooking === true)
);

export const currentConsumerPcpIdListSelector = createSelector(
  currentConsumerPcpDoctorsSelector,
  docs => docs.map(doc => doc.corpProvId)
);

export const currentConsumerPastIdListSelector = createSelector(
  currentConsumerPastDoctorsSelector,
  docs => docs.map(doc => doc.corpProvId)
);

export const currentConsumerPcpSelector = createSelector(
  [pcpDoctorConsumerSelector, currentAccountConsumerIdSelector],
  (pcpDoctors, consumerId) => pcpDoctors.filter(dr => dr.consumerIdList.includes(consumerId))
);

export const currentConsumerIntermountainPcpSelector = createSelector(
  currentConsumerPcpDoctorsSelector,
  pcpDoctors => pcpDoctors.find(doctor => doctor.isICentraPcp)
);

export const canEditMyPcp = createSelector(currentConsumerPcpSelector, consumerPcp =>
  consumerPcp.some(pcp => pcp.isEditable)
);

export const myDoctorsRequestStateSelector = (state: RootState) =>
  get(state, 'myDoctors.myDoctorRequestState');

export const searchFormattedMyDoctors = createSelector(currentConsumerAllDoctorsSelector, data => {
  return data.map(item => {
    return {
      title: item?.displayName,
      provider: item?.primarySpecialty?.specialtyName,
      location: item?.city,
      date: '',
      category: 'myDoctors',
      icon: 'MyDoctors',
      keywords: ['doctor', 'doctors', 'my doctor', item?.primarySpecialty?.specialtyDesc],
      data: item,
      navWebPath: `/u/provider/${item?.corpProvId}`
    };
  });
});
